/**
 * CardZoomController - Controlador genérico de zoom para tarjetas
 * 
 * Funciona con cualquier tipo de tarjeta (tickets, clientes, etc.)
 * Características:
 * - Zoom a pantalla completa en móvil
 * - Highlight con ring en desktop
 * - Edición inline opcional (si hay campos editables)
 * - Click fuera para cerrar
 * 
 * Uso:
 *   data-controller="card-zoom"
 *   data-card-zoom-target="card"
 *   data-card-zoom-card-id-value="123"           (ID del recurso)
 *   data-card-zoom-update-url-value="/path/:id"  (URL para PATCH, opcional)
 *   data-card-zoom-resource-name-value="ticket"  (nombre del recurso para params, opcional)
 *   data-action="click->card-zoom#toggle input->card-zoom#handleInput ..."
 */
import { Controller } from "@hotwired/stimulus"

// Persiste el estado de zoom cuando Turbo Stream reemplaza la card (button_to dropdowns etc.)
let _pendingZoomCardId = null
let _pendingZoomScrollPosition = null

export default class extends Controller {
  static targets = ["card", "actions", "actionsExtra", "editableField", "closeButton", "fieldsContainer", "lazyFrame"]
  static values = { 
    cardId: Number,           // ID genérico del recurso
    updateUrl: String,        // URL para actualizaciones PATCH (opcional)
    resourceName: String,     // Nombre del recurso para params: ticket, customer, etc.
    detailUrl: String         // URL de detalle para navegación (opcional)
  }

  initialize() {
    this.handleOutsideClick = this.handleOutsideClick.bind(this)
    this.outsideListenerActive = false
  }

  connect() {
    this.activeCard = null
    
    // Listen for edit requests (e.g., from map markers for address field)
    this.handleEditRequest = this.handleEditRequest.bind(this)
    window.addEventListener('edit-card-address', this.handleEditRequest)
    // Mantener compatibilidad con tickets
    window.addEventListener('edit-ticket-address', this.handleEditRequest)
    
    // Listen for geocode completion to update address field styling
    this.handleGeocodeCompleted = this.handleGeocodeCompleted.bind(this)
    window.addEventListener('geocode-completed', this.handleGeocodeCompleted)
    
    // Handle virtual keyboard on mobile - prevent zoom issues
    this.handleViewportResize = this.handleViewportResize.bind(this)
    this.handleFocusIn = this.handleFocusIn.bind(this)
    this.handleFocusOut = this.handleFocusOut.bind(this)

    // Interceptar Turbo Streams que reemplacen esta card mientras está zoomed
    // (para otros endpoints that not dropdowns, e.g., direct API calls)
    this.handleBeforeStreamRender = this.handleBeforeStreamRender.bind(this)
    document.addEventListener('turbo:before-stream-render', this.handleBeforeStreamRender)

    // Re-zoom si esta card fue reemplazada por un Turbo Stream mientras estaba zoomed
    if (_pendingZoomCardId !== null && _pendingZoomCardId === this.cardIdValue) {
      const savedScroll = _pendingZoomScrollPosition
      _pendingZoomCardId = null
      _pendingZoomScrollPosition = null
      requestAnimationFrame(() => {
        // Restore saved scroll position so resetCard() can restore it correctly on close
        if (savedScroll !== null) this.savedScrollPosition = savedScroll
        this.toggle({ currentTarget: this.element, target: this.element, stopPropagation: () => {} })
      })
    }
  }
  
  disconnect() {
    // Si estamos siendo reemplazados por Turbo Stream mientras estábamos zoomed,
    // NO hacer closeActiveCard (el estado de cuerpo/scroll se preserva para el re-zoom)
    if (_pendingZoomCardId !== this.cardIdValue) {
      this.closeActiveCard()
    }
    document.removeEventListener('turbo:before-stream-render', this.handleBeforeStreamRender)
    window.removeEventListener('edit-card-address', this.handleEditRequest)
    window.removeEventListener('edit-ticket-address', this.handleEditRequest)
    window.removeEventListener('geocode-completed', this.handleGeocodeCompleted)
    this.removeKeyboardListeners()
  }

  // Detecta si un Turbo Stream va a reemplazar esta card mientras está zoomed.
  // Guarda el estado para que connect() re-zoom la nueva card al insertarse en el DOM.
  handleBeforeStreamRender(event) {
    if (!this.element.classList.contains('zoomed')) return
    // En Turbo 8 el stream element está en event.detail.newStream y también en event.target
    const streamEl = event.detail?.newStream || event.target
    if (!streamEl) return
    const action = streamEl.getAttribute('action')
    const target = streamEl.getAttribute('target')
    if ((action === 'replace' || action === 'update') && target === `ticket_${this.cardIdValue}`) {
      _pendingZoomCardId = this.cardIdValue
      _pendingZoomScrollPosition = this.savedScrollPosition ?? null
      // NO tocamos event.detail.render — dejar que Turbo reemplace el DOM normalmente.
      // disconnect() saltará closeActiveCard(), connect() volverá a hacer zoom.
    }
  }

  // ===== VIRTUAL KEYBOARD HANDLING =====
  
  addKeyboardListeners() {
    // Use visualViewport API if available (better accuracy)
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', this.handleViewportResize)
    }
    // Also listen to focus events as backup
    document.addEventListener('focusin', this.handleFocusIn)
    document.addEventListener('focusout', this.handleFocusOut)
  }
  
  removeKeyboardListeners() {
    if (window.visualViewport) {
      window.visualViewport.removeEventListener('resize', this.handleViewportResize)
    }
    document.removeEventListener('focusin', this.handleFocusIn)
    document.removeEventListener('focusout', this.handleFocusOut)
  }
  
  handleViewportResize() {
    // Called when visualViewport changes (keyboard appearing/disappearing)
    if (!this.activeCard || window.innerWidth >= 768) return
    
    const viewportHeight = window.visualViewport?.height || window.innerHeight
    const offsetTop = window.visualViewport?.offsetTop || 0
    const headerHeight = 40
    
    // Adjust card position to account for keyboard
    this.activeCard.style.height = `${viewportHeight - headerHeight}px`
    this.activeCard.style.top = `${headerHeight + offsetTop}px`
  }
  
  handleFocusIn(event) {
    if (!this.activeCard || window.innerWidth >= 768) return
    
    const isInput = event.target.matches('input, textarea, select')
    if (!isInput) return
    
    // Store that we have an input focused
    this.inputFocused = true
    
    // Small delay to let keyboard appear
    setTimeout(() => {
      if (!this.inputFocused || !this.activeCard) return
      
      // Ensure the card stays fixed
      this.activeCard.style.position = 'fixed'
      
      // Scroll the input into view within the card
      const inputRect = event.target.getBoundingClientRect()
      const cardRect = this.activeCard.getBoundingClientRect()
      
      // If input is below the visible area, scroll to it
      if (inputRect.bottom > cardRect.bottom - 20) {
        event.target.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    }, 300)
  }
  
  handleFocusOut(event) {
    if (!this.activeCard || window.innerWidth >= 768) return
    
    const isInput = event.target.matches('input, textarea, select')
    if (!isInput) return
    
    this.inputFocused = false
    
    // Restore card dimensions after keyboard hides
    setTimeout(() => {
      if (this.inputFocused || !this.activeCard) return
      
      const headerHeight = 40
      this.activeCard.style.height = `calc(100vh - ${headerHeight}px)`
      this.activeCard.style.top = `${headerHeight}px`
    }, 100)
  }

  // Prevent click from propagating to parent (used for links inside cards)
  stopPropagation(event) {
    event.stopPropagation()
  }
  
  // Handle edit request from external sources (e.g., map markers, warnings panel)
  handleEditRequest(event) {
    const eventId = event.detail.ticketId || event.detail.cardId
    if (eventId !== this.cardIdValue) return

    const fieldName = event.detail.field || 'direccion_reparacion'
    
    // Scroll to the card if not in viewport
    this.element.scrollIntoView({ behavior: 'smooth', block: 'center' })
    
    // First, zoom the card if not already zoomed
    if (!this.element.classList.contains('zoomed')) {
      this.toggle({ currentTarget: this.element, stopPropagation: () => {}, target: this.element })
    }

    // Small delay to ensure zoom animation completes
    setTimeout(() => {
      // Try to find existing field
      let field = this.editableFieldTargets.find(f => f.dataset.field === fieldName)
      
      // If field doesn't exist, create it dynamically
      if (!field) {
        field = this.createEmptyField(fieldName)
      }
      
      if (field) {
        field.removeAttribute('readonly')
        field.focus()
        field.select()
        // Highlight the field to draw attention
        field.classList.add('ring-2', 'ring-blue-400', 'bg-blue-50')
        setTimeout(() => {
          field.classList.remove('ring-2', 'ring-blue-400', 'bg-blue-50')
        }, 2000)
      }
    }, 200)
  }

  // Handle geocode completion event
  handleGeocodeCompleted(event) {
    const { ticketId, status } = event.detail
    
    // Only update if it's for this card
    if (ticketId !== this.cardIdValue) return
    
    // Find the address field
    const addressField = this.editableFieldTargets.find(f => f.dataset.field === 'direccion_reparacion')
    if (!addressField) return
    
    // Remove spinner
    addressField.classList.remove('opacity-50')
    const spinner = addressField.parentElement.querySelector('.geocode-spinner')
    if (spinner) spinner.remove()
    
    // Update field styling based on geocode result
    if (status === 'success') {
      // Remove error styling
      addressField.classList.remove('border-amber-200', 'border-amber-300')
      addressField.classList.add('border-transparent')
      
      // Update icon color (find parent div, then the svg)
      const iconSpan = addressField.parentElement.querySelector('svg')
      if (iconSpan) {
        iconSpan.classList.remove('text-amber-500')
        iconSpan.classList.add('text-gray-500')
      }
      
      // Flash green ring briefly
      addressField.classList.add('ring-2', 'ring-green-400')
      setTimeout(() => {
        addressField.classList.remove('ring-2', 'ring-green-400')
      }, 1500)
    } else {
      // Keep error styling (subtle amber border)
      addressField.classList.add('border-amber-200')
      
      // Flash red ring
      addressField.classList.add('ring-2', 'ring-red-400')
      setTimeout(() => {
        addressField.classList.remove('ring-2', 'ring-red-400')
      }, 1500)
    }
  }
  
  // Create an empty editable field dynamically when it doesn't exist
  createEmptyField(fieldName) {
    // Find the container for contact/location info using the target
    const infoContainer = this.hasFieldsContainerTarget 
      ? this.fieldsContainerTarget 
      : this.element.querySelector('.text-sm.text-gray-600.space-y-1')
    
    if (!infoContainer) {
      console.warn('CardZoom: Could not find fields container')
      return null
    }

    // Field configuration
    const fieldConfig = {
      nombre: { icon: '<svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/></svg>', placeholder: 'Añadir nombre...' },
      apellido: { icon: '<svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/></svg>', placeholder: 'Añadir apellido...' },
      telefono: { icon: '<svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/></svg>', placeholder: 'Añadir teléfono...' },
      email: { icon: '<svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg>', placeholder: 'Añadir email...' },
      numero_bici: { icon: '<svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5"><circle cx="5.5" cy="17.5" r="3.5"/><circle cx="18.5" cy="17.5" r="3.5"/><path d="M15 6h2l3 11.5M5.5 17.5l3-8h5.5l2 5M8.5 9.5L12 6"/></svg>', label: 'Bici nº', placeholder: 'Nº' },
      direccion_reparacion: { icon: '<svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/></svg>', placeholder: 'Añadir dirección...' }
    }

    const config = fieldConfig[fieldName]
    if (!config) return null

    // Create the field wrapper
    const wrapper = document.createElement('div')
    wrapper.className = 'flex items-center gap-1 w-full md:max-w-[280px] min-w-0'
    wrapper.dataset.dynamicField = fieldName

    // Create icon span
    const iconSpan = document.createElement('span')
    iconSpan.className = 'flex-shrink-0 px-1 py-0.5'
    iconSpan.innerHTML = config.icon
    wrapper.appendChild(iconSpan)

    // Add label if exists
    if (config.label) {
      const labelSpan = document.createElement('span')
      labelSpan.className = 'text-sm px-1 py-0.5'
      labelSpan.textContent = config.label
      wrapper.appendChild(labelSpan)
    }

    // Create input field
    const input = document.createElement('input')
    input.type = 'text'
    input.dataset.cardZoomTarget = 'editableField'
    input.dataset.field = fieldName
    input.dataset.originalValue = ''
    input.className = 'editable-field bg-transparent border-2 border-blue-300 px-1 py-0.5 rounded transition-all text-sm pointer-events-auto cursor-text hover:bg-gray-50 focus:outline-none focus:border-blue-500 focus:bg-white min-w-0 flex-1'
    input.placeholder = config.placeholder
    input.size = 20
    wrapper.appendChild(input)

    // Insert at appropriate position in container
    infoContainer.appendChild(wrapper)

    // Register the new field target
    this.editableFieldTargets.push(input)

    return input
  }

  // ===== INLINE EDITING WITH EVENT DELEGATION =====
  
  // Handle input event - auto-resize fields
  handleInput(event) {
    const field = event.target
    if (!field.classList.contains('editable-field')) return
    if (!this.element.classList.contains('zoomed')) return
    
    if (field.tagName === 'INPUT') {
      const charCount = Math.max(field.value.length, 1)
      field.size = charCount
    } else if (field.tagName === 'TEXTAREA') {
      field.style.height = 'auto'
      field.style.height = field.scrollHeight + 'px'
    }
  }
  
  // Handle blur - submit if changed
  async handleBlur(event) {
    const field = event.target
    if (!field.classList.contains('editable-field')) return
    if (!this.element.classList.contains('zoomed')) return
    
    await this.submitField(field)
  }
  
  // Handle keydown - Enter to submit, Escape to cancel
  handleKeydown(event) {
    const field = event.target
    if (!field.classList.contains('editable-field')) return
    if (!this.element.classList.contains('zoomed')) return
    
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      field.blur()
    } else if (event.key === 'Escape') {
      field.value = field.dataset.originalValue || ''
      field.blur()
    }
  }
  
  // Submit field change to server
  async submitField(field) {
    // Solo si hay URL de actualización configurada
    if (!this.hasUpdateUrlValue || !this.updateUrlValue) return
    
    const fieldName = field.dataset.field
    const originalValue = field.dataset.originalValue || ''
    const newValue = field.value.trim()
    
    if (newValue === originalValue) return
    
    field.classList.add('opacity-50')
    
    try {
      const resourceName = this.resourceNameValue || 'record'
      const response = await fetch(this.updateUrlValue, {
        method: 'PATCH',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify({
          [resourceName]: {
            [fieldName]: newValue
          }
        })
      })

      if (response.ok) {
        field.dataset.originalValue = newValue
        
        // For address field, keep opacity and add spinner (geocoding in progress)
        if (fieldName === 'direccion_reparacion') {
          // Add spinner icon
          const spinner = document.createElement('div')
          spinner.className = 'geocode-spinner absolute right-1 top-1/2 -translate-y-1/2'
          spinner.innerHTML = '<svg class="animate-spin h-4 w-4 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>'
          
          // Make parent relative if not already
          const parent = field.parentElement
          if (parent && !parent.classList.contains('relative')) {
            parent.classList.add('relative')
          }
          parent?.appendChild(spinner)
          
          window.dispatchEvent(new CustomEvent('address-updated', { 
            detail: { cardId: this.cardIdValue, ticketId: this.cardIdValue } 
          }))
        } else {
          // For non-address fields, show success immediately
          field.classList.remove('opacity-50')
          field.classList.add('ring-2', 'ring-green-400')
          setTimeout(() => {
            field.classList.remove('ring-2', 'ring-green-400')
          }, 500)
        }
        
      } else {
        field.value = originalValue
        field.classList.remove('opacity-50')
        field.classList.add('ring-2', 'ring-red-400')
        setTimeout(() => {
          field.classList.remove('ring-2', 'ring-red-400')
        }, 1000)
      }
    } catch (error) {
      console.error('Error updating field:', error)
      field.value = originalValue
      field.classList.remove('opacity-50')
      field.classList.add('ring-2', 'ring-red-400')
      setTimeout(() => {
        field.classList.remove('ring-2', 'ring-red-400')
      }, 1000)
    }
  }

  // ===== ZOOM FUNCTIONALITY =====

  toggle(event) {
    const newCard = event.currentTarget
    if (!newCard) return

    // Ignorar clicks en botones, links, inputs y otros elementos interactivos
    const clickedElement = event.target
    const isInteractiveElement = clickedElement.closest('button, a, input, select, textarea, [data-controller*="ticket-resolution"]')
    
    if (isInteractiveElement) return

    // Si la card ya está activa (zoomed), ignorar clicks adicionales
    if (newCard.classList.contains('zoomed')) return

    event.stopPropagation()

    const isMobile = window.innerWidth < 768
    
    // En móvil, ocultar la tarjeta INMEDIATAMENTE con visibility (más rápido que opacity)
    if (isMobile) {
      newCard.style.visibility = 'hidden'
    }

    // Si hay una tarjeta activa diferente, desactivarla primero
    if (this.activeCard && this.activeCard !== newCard) {
      this.hideActions(this.activeCard)
      this.resetCard(this.activeCard)
    }

    // Limpiar cualquier otra tarjeta zoomed
    document.querySelectorAll(".zoomed").forEach((card) => {
      if (card !== newCard) {
        this.hideActions(card)
        this.resetCard(card)
      }
    })

    this.removeOutsideClickListener()
    
    if (isMobile) {
      // Guardar posición de scroll actual para restaurarla después
      this.savedScrollPosition = window.scrollY
      
      // Bloquear scroll del body y documentElement
      document.body.style.overflow = 'hidden'
      document.documentElement.style.overflow = 'hidden'
      document.body.style.position = 'fixed'
      document.body.style.width = '100%'
      document.body.style.top = `-${this.savedScrollPosition}px`
      
      // Scroll al top DESPUÉS de bloquear el scroll (para que no se vea)
      window.scrollTo({ top: 0, behavior: 'instant' })
      
      // El navbar tiene h-10 = 40px
      const navbarHeight = 40
      
      // Detectar la altura de los tabs de bike_hunter (Cronograma/Ruta/Advertencias)
      // Los tabs están fixed debajo del navbar con py-3 y altura de botones
      const tabsContainer = document.querySelector('[data-bike-hunter-tabs-target="tab"]')?.closest('.max-md\\:fixed')
      const tabsHeight = tabsContainer ? tabsContainer.offsetHeight : 0
      
      // Altura total del header (navbar + tabs si existen)
      const headerHeight = navbarHeight + tabsHeight
      
      // Ocultar el mobile-header (estadísticas/filtros) para que no tape el ticket
      this.hideMobileHeader()
      
      // Aplicar estilos de posición
      newCard.style.position = 'fixed'
      newCard.style.top = `${headerHeight}px`
      newCard.style.left = '0'
      newCard.style.width = '100vw'
      newCard.style.height = `calc(100vh - ${headerHeight}px)`
      newCard.style.zIndex = "9998"
      newCard.style.margin = '0'
      newCard.style.borderRadius = '0'
      // Permitir scroll vertical cuando el contenido es más alto que el viewport
      // NOTA: Inicialmente deshabilitamos el scroll, se habilitará cuando se abra el form de resolución
      newCard.style.overflowY = 'hidden'
      newCard.style.overflowX = 'hidden'
      newCard.style.webkitOverflowScrolling = 'touch' // Para cuando se habilite el scroll
      newCard.style.padding = '1rem'
      newCard.style.display = 'flex'
      newCard.style.flexDirection = 'column'
      // Use flex-start to prevent overlap when content is taller than viewport
      // Center is problematic because it compresses content vertically
      newCard.style.justifyContent = 'flex-start'
      newCard.style.opacity = '0'
      newCard.style.transition = 'opacity 0.12s ease-out'
      // CRÍTICO: Habilitar pointer-events para que la card sea interactiva
      newCard.style.pointerEvents = 'auto'
      
      this.addCloseButton(newCard)
      newCard.classList.add('mobile-zoom-active', 'shadow-2xl', 'zoomed')
      
      // Add keyboard listeners to handle virtual keyboard
      this.addKeyboardListeners()
      
      // Hacer visible y fade-in en el siguiente frame
      requestAnimationFrame(() => {
        newCard.style.visibility = 'visible'
        requestAnimationFrame(() => {
          newCard.style.opacity = '1'
          
          // Verificar si el contenido excede el viewport y activar scroll automáticamente
          // Esperar un frame adicional para que el contenido se haya renderizado completamente
          requestAnimationFrame(() => {
            if (newCard.scrollHeight > newCard.clientHeight) {
              // El contenido es más alto que el contenedor, habilitar scroll
              newCard.style.setProperty('overflow-y', 'auto', 'important')
              newCard.style.setProperty('-webkit-overflow-scrolling', 'touch', 'important')
            }
          })
        })
      })
    } else {
      newCard.style.zIndex = "50"
      newCard.classList.add("ring-4", "ring-orange-400", "shadow-2xl", "zoomed")
      
      // Centrar la card en la pantalla (solo desktop)
      setTimeout(() => {
        newCard.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }, 50)
    }
    
    newCard.classList.remove("cursor-pointer", "hover:shadow-lg", "hover:scale-[1.02]")

    // Mostrar acciones si existen
    const actions = newCard.querySelector("[data-card-zoom-target='actions']")
    if (actions) {
      actions.classList.remove("hidden")
      actions.classList.add("flex")

      // Cargar notas si existe el controller ticket-notas
      const notasController = this.application.getControllerForElementAndIdentifier(actions, "ticket-notas")
      if (notasController && typeof notasController.load === "function") {
        notasController.load()
      }
    }
    
    // Mostrar acciones extra si existen
    const actionsExtra = newCard.querySelector("[data-card-zoom-target='actionsExtra']")
    if (actionsExtra) {
      actionsExtra.classList.remove("hidden")
    }

    // Recargar turbo-frames lazy para obtener datos actualizados (ej: badge con Chargebee)
    this.reloadLazyFrames(newCard)

    this.activeCard = newCard
    this.addOutsideClickListener()
    this.enableEditing(newCard)
  }
  
  // Método close para usar desde botones
  close(event) {
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }
    this.closeActiveCard()
  }
  
  enableEditing(card) {
    const fields = card.querySelectorAll('.editable-field')
    fields.forEach(field => {
      field.removeAttribute('readonly')
      if (field.tagName === 'TEXTAREA') {
        requestAnimationFrame(() => {
          field.style.height = 'auto'
          field.style.height = field.scrollHeight + 'px'
        })
      }
    })
  }
  
  disableEditing(card) {
    const fields = card.querySelectorAll('.editable-field')
    fields.forEach(field => {
      field.setAttribute('readonly', '')
      if (field.tagName === 'TEXTAREA') {
        field.style.height = ''
      }
    })
  }

  addCloseButton(card) {
    // Ocultar contenido normal del navbar y mostrar botón "Volver"
    const navContent = document.getElementById('global-nav-content')
    const backBtn = document.getElementById('global-nav-back-btn')
    
    if (navContent && backBtn) {
      navContent.classList.add('hidden')
      backBtn.classList.remove('hidden')
      backBtn.classList.add('flex')
      
      // Usar AbortController para poder cancelar el listener después
      if (this.backBtnAbortController) {
        this.backBtnAbortController.abort()
      }
      this.backBtnAbortController = new AbortController()
      
      backBtn.addEventListener('click', (e) => {
        e.stopPropagation()
        this.closeActiveCard()
      }, { signal: this.backBtnAbortController.signal })
    }
    
    // También mostrar closeButton target si existe (para tarjetas con botón propio)
    if (this.hasCloseButtonTarget) {
      this.closeButtonTarget.classList.remove('hidden')
    }
  }

  removeCloseButton(card) {
    // Restaurar contenido normal del navbar y ocultar botón "Volver"
    const navContent = document.getElementById('global-nav-content')
    const backBtn = document.getElementById('global-nav-back-btn')
    
    if (navContent && backBtn) {
      navContent.classList.remove('hidden')
      backBtn.classList.add('hidden')
      backBtn.classList.remove('flex')
    }
    
    // Cancelar el listener del botón
    if (this.backBtnAbortController) {
      this.backBtnAbortController.abort()
      this.backBtnAbortController = null
    }
    
    if (this.hasCloseButtonTarget) {
      this.closeButtonTarget.classList.add('hidden')
    }
  }

  resetCard(card) {
    this.disableEditing(card)
    
    const isMobile = window.innerWidth < 768
    
    if (isMobile) {
      card.style.position = ''
      card.style.top = ''
      card.style.left = ''
      card.style.width = ''
      card.style.height = ''
      card.style.margin = ''
      card.style.borderRadius = ''
      card.style.overflowY = ''
      card.style.overflowX = ''
      card.style.padding = ''
      card.style.display = ''
      card.style.flexDirection = ''
      card.style.justifyContent = ''
      card.style.opacity = ''
      card.style.transition = ''
      card.style.visibility = ''
      card.style.pointerEvents = ''
      
      // Restaurar scroll en body y html
      document.body.style.overflow = ''
      document.documentElement.style.overflow = ''
      document.body.style.position = ''
      document.body.style.width = ''
      document.body.style.top = ''
      
      // Restaurar posición de scroll original
      if (this.savedScrollPosition !== undefined) {
        window.scrollTo(0, this.savedScrollPosition)
        this.savedScrollPosition = undefined
      }
      
      // Restaurar el mobile-header
      this.showMobileHeader()
      
      // Remove keyboard listeners
      this.removeKeyboardListeners()
      
      this.removeCloseButton(card)
      card.classList.remove('mobile-zoom-active')
    }
    
    card.style.zIndex = "1"
    card.classList.remove("ring-4", "ring-orange-400", "shadow-2xl", "zoomed")
    card.classList.remove("pointer-events-none")
    card.classList.add("cursor-pointer", "hover:shadow-lg", "hover:scale-[1.02]")
  }

  hideActions(card) {
    const actions = card?.querySelector("[data-card-zoom-target='actions']")
    if (actions && !actions.classList.contains("hidden")) {
      actions.classList.add("hidden")
      actions.classList.remove("flex")
    }
    
    // Ocultar acciones extra
    const actionsExtra = card?.querySelector("[data-card-zoom-target='actionsExtra']")
    if (actionsExtra && !actionsExtra.classList.contains("hidden")) {
      actionsExtra.classList.add("hidden")
    }
  }

  handleOutsideClick(event) {
    if (!this.activeCard) return
    if (this.activeCard.contains(event.target)) return
    this.closeActiveCard()
  }

  addOutsideClickListener() {
    if (this.outsideListenerActive) return
    document.addEventListener("click", this.handleOutsideClick, true)
    this.outsideListenerActive = true
  }

  removeOutsideClickListener() {
    if (!this.outsideListenerActive) return
    document.removeEventListener("click", this.handleOutsideClick, true)
    this.outsideListenerActive = false
  }

  closeActiveCard() {
    if (!this.activeCard) return
    // Limpiar el pending zoom para que, cuando los renders diferidos disparen
    // el disconnect/connect del controller, NO se vuelva a hacer re-zoom.
    if (_pendingZoomCardId === this.cardIdValue) {
      _pendingZoomCardId = null
      _pendingZoomScrollPosition = null
    }
    this.hideActions(this.activeCard)
    this.resetCard(this.activeCard)
    this.activeCard = null
    this.removeOutsideClickListener()
  }

  // Recargar turbo-frames lazy dentro de la card para obtener datos actualizados
  reloadLazyFrames(card) {
    const lazyFrames = card.querySelectorAll('turbo-frame[loading="lazy"][src]')
    lazyFrames.forEach(frame => {
      // Forzar recarga del frame
      const src = frame.getAttribute('src')
      if (src) {
        frame.setAttribute('src', '')
        frame.setAttribute('src', src)
      }
    })
  }

  // Ocultar el mobile-header (estadísticas/filtros) cuando se hace zoom en móvil
  hideMobileHeader() {
    const mobileHeader = document.querySelector('[data-controller="mobile-header"]')
    if (mobileHeader) {
      mobileHeader.style.display = 'none'
    }
  }

  // Restaurar el mobile-header cuando se cierra el zoom
  showMobileHeader() {
    const mobileHeader = document.querySelector('[data-controller="mobile-header"]')
    if (mobileHeader) {
      mobileHeader.style.display = ''
    }
  }
}
